import {
  BrandDefenseFilled,
  BrandSellInFilled,
  CategoryReviewFilled,
  DiagnosingGrowthFilled,
  formatData,
  IncrementalityFilled,
  WhosMyShopperFilled,
} from '@utilities';
import {
  COLOR_BANTAMWEIGHT,
  COLOR_DEEPTEAL,
  COLOR_NUMERATOR_GREEN,
  COLOR_SAPPHIRE,
  COLOR_SCALE_0,
  COLOR_WELTERWEIGHT,
} from '@utilities/helpers/colors';
import { transformColumn } from '@api/fast_tracks/utilities/helpers';
import IndexBar from '../../../pages/Report/components/Tab/components/Visualizations/components/IndexBar';
import { fastTrackStatus } from '@helpers/fast_tracks';
import {
  HELP_CENTER_BASE_URL,
  HELP_CENTER_NARRATIVE_URLS,
} from '../../../pages/FastTracks/utilities/constants';

/**
 * Returns the default structure for topic data.
 * This includes default initial error state, visibility, loading state, and title.
 *
 * @function getDefaultTopicData
 * @param {Object} topic
 * @returns {Object} - Initial topic data structure
 */
export const getDefaultTopicData = (topic) => {
  return {
    error: false,
    isHidden: topic.isHidden,
    loading: true,
    tags: topic.tags,
    title: topic.title,
  };
};

/**
 * Maps data variant to specific colors
 * @param {string} variant - The data variant ('primary', 'secondary', 'other')
 * @returns {string} - The corresponding color hex code
 */
export const getColorByVariant = (variant) => {
  const colorMap = {
    other: COLOR_SAPPHIRE,
    primary: COLOR_NUMERATOR_GREEN,
    secondary: COLOR_DEEPTEAL,
  };

  return colorMap[variant?.toLowerCase()] || COLOR_SCALE_0;
};

/**
 * Transforms the data for the table
 * @param {Object} options
 * @param {Object[]} options.columnsData
 * @param {Object} options.rowData
 * @return {Object[]}
 */

export const getTableColumns = (columnsData) => {
  const columns = columnsData.map((col) => {
    const column = transformColumn(col);
    const hasIndexBar = column.indexCol;

    return {
      ...column,
      // eslint-disable-next-line react/prop-types
      Cell: ({ getValue }) => {
        const value = formatData({
          format: column.format,
          value: getValue() || 'NAN_SENTINEL',
        });

        if (!hasIndexBar) return value;

        return (
          <span
            style={{
              alignItems: 'center',
              display: 'flex',
            }}
          >
            {value}
            <IndexBar value={getValue()} />
          </span>
        );
      },
      width: hasIndexBar ? 200 : column.width,
    };
  });

  return columns;
};

/**
 * return the appropriate load header depending on the flow status
 *
 * @function loaderHeader
 * @param {string} flow status
 * @returns {string} the header of the load component
 */
export const loaderHeader = (status) => {
  switch (status) {
    case fastTrackStatus.QUEUED:
      return 'In Queue';
    case fastTrackStatus.SETUP:
      return 'Setup';
    default:
      return '';
  }
};

/**
 * return the appropriate load message depending on the flow status
 *
 * @function loaderMessage
 * @param {Flow} flow status
 * @returns {string} the message of the load component
 */
export const loaderMessage = (flow) => {
  if (!flow) {
    return [''];
  }

  switch (flow?.status) {
    case fastTrackStatus.SETUP:
      return ['Validating Sample'];
    default:
      return [''];
  }
};

/**
 * Returns the icon component based on the FastTrack ID
 * @param {string} fastTrackId - The ID of the FastTrack topic
 * @returns {React.Component|null} - The corresponding icon component or null if not found
 */
export const getIconByFastTrackId = (fastTrackId) => {
  const iconMap = {
    'brand-defense': <BrandDefenseFilled />,
    'brand-sell-in': <BrandSellInFilled />,
    'category-review': <CategoryReviewFilled />,
    'diagnosing-growth': <DiagnosingGrowthFilled />,
    incrementality: <IncrementalityFilled />,
    'who-is-my-shopper': <WhosMyShopperFilled />,
  };

  return iconMap[fastTrackId] || null;
};

/**
 * Returns structured data for different chart types to add to PowerPoint slides
 * @param {Object} dataset
 * @returns {Object}
 */
export const getPptStructuredData = (dataset) => {
  switch (dataset?.type) {
    case 'bar-chart':
    case 'bar-chart-two-labels':
    case 'column-chart':
    case 'bar-stack-chart':
      return {
        barDir: ['bar-chart', 'bar-chart-two-labels', 'bar-stack-chart'].includes(dataset.type)
          ? 'bar'
          : 'col',
        barGrouping: dataset.type === 'bar-stack-chart' ? 'percentStacked' : null,
        format: dataset?.yAxis?.labels?.format,
        series: dataset?.series,
        type: dataset?.type,
        xAxis: dataset?.xAxis,
      };
    case 'scatter-chart':
      return {
        formatX: dataset?.xAxis?.labels?.format,
        formatY: dataset?.yAxis?.labels?.format,
        nameX: dataset.xAxis?.title?.text || '',
        nameY: dataset?.yAxis?.title?.text || '',
        series: dataset?.series,
        type: dataset?.type,
        xAxis: dataset?.xAxis,
      };
    case 'table':
      return {
        columns: dataset?.columns,
        data: dataset?.data,
        type: dataset?.type,
      };
    case 'brand-tree-v2':
      return {
        type: 'brand-tree-v2',
      };
    default:
      // Only suitable for highchart components
      return {
        type: 'chart-image',
      };
  }
};

/**
 * Returns the help article URL based on the FastTrack ID
 * @param {string} fastTrackId
 * @returns {string}
 */
export const getHelpArticleURLByFastTrackId = (fastTrackId) => {
  return HELP_CENTER_NARRATIVE_URLS[fastTrackId] || HELP_CENTER_BASE_URL;
};

// Temporary preview data for visualization preview cards
const PREVIEW_BAR_CATEGORIES = [
  'Retailer A',
  'Retailer B',
  'Retailer C',
  'Retailer D',
  'Retailer E',
  'Retailer F',
];
const PREVIEW_BAR_PRIMARY_VALUES = [92, 82, 63, 48, 36, 22];
const PREVIEW_BAR_SECONDARY_VALUES = [78, 66, 50, 42, 30, 18];
const PREVIEW_BAR_TWO_LABEL_DELTAS = [1.1, -0.6, 0.4, -1.0, 0.3, 0.8];

const PREVIEW_STACK_CATEGORIES = ['Product A', 'Product B', 'Product C', 'Product D', 'Product E'];

const PREVIEW_TABLE_ROWS = [
  {
    definition: 'Total spend expected this quarter',
    metric: 'Projected Sales',
  },
  {
    definition: 'Avg. spend per household',
    metric: 'Buy Rate',
  },
  {
    definition: 'Trips per household',
    metric: 'Purchase Frequency',
  },
  {
    definition: 'Units purchased each visit',
    metric: 'Units per Trip',
  },
];

const PREVIEW_TREE_LEVELS = [
  [
    {
      description: 'Total spend on Ready To Drink Coffee at Any Store',
      metrics: [
        ['Projected Sales', 'Store'],
        ['Percent Change', 'vs Year Ago'],
      ],
      title: 'Projected Sales',
    },
  ],
  [
    {
      description: 'Households buying the category at Any Store',
      metrics: [
        ['Projected Households', 'Any Store'],
        ['Percent Change', 'vs Year Ago'],
      ],
      title: 'Projected Households',
    },
    {
      description: 'Average spend per household at Any Store',
      metrics: [
        ['Buy Rate', 'Per Household'],
        ['Percent Change', 'vs Year Ago'],
      ],
      title: 'Buy Rate',
    },
  ],
  [
    {
      description: 'Trips per household to buy the category',
      metrics: [
        ['Purchase Frequency', 'Trips'],
        ['Percent Change', 'vs Year Ago'],
      ],
      title: 'Purchase Frequency',
    },
    {
      description: 'Total spend per trip for the category',
      metrics: [
        ['Spend per Trip', 'Store'],
        ['Percent Change', 'vs Year Ago'],
      ],
      title: 'Spend per Trip',
    },
    {
      description: 'Average units purchased on each trip',
      metrics: [
        ['Units per Trip', 'Store'],
        ['Percent Change', 'vs Year Ago'],
      ],
      title: 'Units per Trip',
    },
  ],
];

const PREVIEW_SCATTER_POINTS = [
  {
    colorType: 'primary',
    name: 'Retailer Focus',
    x: 32,
    y: 58,
  },
  {
    colorType: 'secondary',
    name: 'Category Leader',
    x: 24,
    y: 44,
  },
  {
    colorType: 'other',
    name: 'Emerging Player',
    x: 15,
    y: 36,
  },
];

const buildPreviewBarSeries = () => [
  {
    color: COLOR_BANTAMWEIGHT,
    data: [...PREVIEW_BAR_PRIMARY_VALUES],
    name: '% of Spend 1YA',
  },
  {
    color: COLOR_WELTERWEIGHT,
    data: [...PREVIEW_BAR_SECONDARY_VALUES],
    name: '% of Spend',
  },
];

const buildPreviewTwoLabelSeries = () => [
  {
    color: COLOR_BANTAMWEIGHT,
    data: PREVIEW_BAR_CATEGORIES.map((category, index) => {
      const change = PREVIEW_BAR_TWO_LABEL_DELTAS[index % PREVIEW_BAR_TWO_LABEL_DELTAS.length];
      return {
        name: category,
        y: PREVIEW_BAR_SECONDARY_VALUES[index],
        y2: change,
        yDelta: change >= 0 ? 1 : -1,
      };
    }),
    dataLabels: [
      {
        format: '0.0%',
      },
      {
        format: '0.0',
      },
    ],
    name: '% of Spend',
  },
];

const buildPreviewStackSeries = () => [
  {
    color: COLOR_BANTAMWEIGHT,
    data: [45, 52, 46, 58, 60],
    name: 'Store Share',
  },
  {
    color: COLOR_WELTERWEIGHT,
    data: [55, 48, 54, 42, 40],
    name: 'Other Retailers',
  },
];

const buildPreviewTableColumns = () => [
  {
    accessor: 'metric',
    Header: 'Metric',
    width: 160,
  },
  {
    accessor: 'current',
    Header: 'Current',
  },
  {
    accessor: 'change',
    Header: 'Change',
  },
  {
    accessor: 'index',
    Header: 'Index',
  },
];

const buildPreviewTableData = () =>
  PREVIEW_TABLE_ROWS.map(({ definition, metric }) => ({
    change: null,
    current: null,
    definition,
    metric,
  }));

const buildPreviewTreeNodes = () => {
  let nodeIndex = 0;
  return PREVIEW_TREE_LEVELS.flatMap((row, rowIndex) =>
    row.map(({ description, metrics, title }) => {
      nodeIndex += 1;
      const node = {
        color: COLOR_BANTAMWEIGHT,
        description,
        header: title,
        node_id: `preview-node-${nodeIndex}`,
        node_row: rowIndex + 1,
        subdescription: '',
      };

      metrics.forEach((metricRow, metricRowIndex) => {
        metricRow.forEach((metric, metricColIndex) => {
          node[`r${metricRowIndex + 1}c${metricColIndex + 1}_description`] = metric;
        });
      });

      return node;
    })
  );
};

const buildPreviewScatterSeries = () => [
  {
    data: PREVIEW_SCATTER_POINTS.map((point) => ({ ...point })),
    dataLabels: {
      enabled: true,
    },
    name: 'Retailers',
  },
];

/**
 * Returns preview dataset for a given chart type, used for preview cards in the Narrative Flow.
 * @param {string} type - The type of the chart (e.g., 'bar-chart', 'scatter-chart', etc.)
 * @returns {Object|null} - The structured dataset for the specified chart type or null if type is unrecognized
 */
export const getPreviewDataset = (type) => {
  switch (type) {
    case 'bar-chart':
      return {
        series: buildPreviewBarSeries(),
        type,
        xAxis: {
          categories: [...PREVIEW_BAR_CATEGORIES],
        },
        yAxis: {
          labels: {
            format: '0.0%',
          },
        },
      };
    case 'bar-chart-two-labels':
      return {
        series: buildPreviewTwoLabelSeries(),
        type,
        xAxis: {
          categories: [...PREVIEW_BAR_CATEGORIES],
        },
        yAxis: {
          labels: {
            format: '0.0%',
          },
        },
      };
    case 'bar-stack-chart':
      return {
        series: buildPreviewStackSeries(),
        type,
        xAxis: {
          categories: [...PREVIEW_STACK_CATEGORIES],
        },
        yAxis: {
          labels: {
            format: '0%',
          },
        },
      };
    case 'column-chart':
      return {
        series: buildPreviewBarSeries(),
        type,
        xAxis: {
          categories: [...PREVIEW_BAR_CATEGORIES],
        },
        yAxis: {
          labels: {
            format: '0.0%',
          },
        },
      };
    case 'scatter-chart':
      return {
        series: buildPreviewScatterSeries(),
        type,
        xAxis: {
          labels: {
            format: '0%',
          },
          max: 60,
          plotLines: [
            {
              color: COLOR_WELTERWEIGHT,
              value: 28,
            },
          ],
          title: {
            text: 'Household Penetration',
          },
        },
        yAxis: {
          labels: {
            format: '$0,0.0',
          },
          max: 80,
          plotLines: [
            {
              color: COLOR_WELTERWEIGHT,
              value: 45,
            },
          ],
          title: {
            text: 'Trips per Buyer',
          },
        },
      };
    case 'table':
      return {
        columns: buildPreviewTableColumns(),
        data: buildPreviewTableData(),
        type,
      };
    case 'brand-tree-v2':
    case 'ebsova-tree':
      return {
        nodes: buildPreviewTreeNodes(),
        type,
      };
    default:
      return null;
  }
};
